import type { ReactNode } from 'react'
import { Radar } from 'lucide-react'
import { MainNav, type NavigationItem } from './MainNav'
import { UserMenu } from './UserMenu'

export interface AppShellProps {
  children?: ReactNode
  navigationItems?: NavigationItem[]
  user?: { name: string; avatarUrl?: string }
  onNavigate?: (href: string) => void
  onLogout?: () => void
  /** When set (admins only), the user menu shows an "Admin" item. */
  onAdmin?: () => void
  /** Extra controls rendered in the header, just left of the user menu (e.g. theme toggle). */
  headerActions?: ReactNode
}

const DEFAULT_NAV: NavigationItem[] = [
  { label: 'Overview', href: '/overview' },
  { label: 'Cash Collections', href: '/cash-collections' },
  { label: 'Sales Report', href: '/sales-report' },
  { label: 'Profit & Loss', href: '/profit-loss' },
  { label: 'DSD Center', href: '/dsd-center' },
]

/**
 * SONAR application shell — sticky top navigation matching the Cash
 * Collections dashboard. Wraps section screen designs via `children`.
 */
export function AppShell({
  children,
  navigationItems = DEFAULT_NAV,
  user = { name: 'DSD Finance' },
  onNavigate,
  onLogout,
  onAdmin,
  headerActions,
}: AppShellProps) {
  return (
    <div
      className="sonar-app min-h-screen bg-stone-50 text-stone-900 dark:bg-stone-950 dark:text-stone-100"
      style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" }}
    >
      <header className="sticky top-0 z-30 border-b border-stone-200/80 bg-white/90 backdrop-blur dark:border-stone-800 dark:bg-stone-900/90">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-3 px-4 sm:px-6">
          {/* Wordmark */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault()
              onNavigate?.('/')
            }}
            className="flex shrink-0 items-center gap-2"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-stone-900 text-champagne-400 dark:bg-champagne-500 dark:text-stone-900">
              <Radar className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="hidden flex-col leading-none sm:flex">
              <span className="text-base font-semibold tracking-tight">SONAR</span>
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-stone-400 dark:text-stone-500">
                Financial Reporting
              </span>
            </span>
          </a>

          {/* Section nav */}
          <div className="flex min-w-0 flex-1 justify-center overflow-x-auto">
            <MainNav items={navigationItems} onNavigate={onNavigate} />
          </div>

          {/* Header actions + user menu */}
          <div className="flex shrink-0 items-center gap-1">
            {headerActions}
            <UserMenu user={user} onLogout={onLogout} onAdmin={onAdmin} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8">{children}</main>
    </div>
  )
}

export default AppShell
